import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import loyaltyService from '../../services/loyaltyService';
import { Award, ChevronRight, Gift, AlertTriangle } from 'lucide-react';

/*
 * One member's card: identity, balances, the points ledger, their vouchers,
 * the reward catalogue as THEY see it (each entry stamped claimable or not,
 * with the reason), and the manager actions — adjust, suspend, claim.
 *
 * The ledger table prints the lot columns (remaining, expires) because that
 * is the answer to the only hard question a member ever asks: which points
 * are about to lapse, and where did the ones that vanished go.
 */

const fmtPts = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const KIND_CLS = {
  EARN: 'text-emerald-700', SIGNUP: 'text-emerald-700', BIRTHDAY: 'text-emerald-700',
  REFERRAL: 'text-emerald-700', TIER_BONUS: 'text-emerald-700', GIFT_CARD: 'text-emerald-700',
  IMPORT: 'text-emerald-700', ADJUST: 'text-sky-700',
  REDEEM: 'text-red-700', EXPIRE: 'text-gray-500', REVERSAL: 'text-red-700',
};

const VOUCHER_CLS = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  REDEEMED: 'bg-gray-100 text-gray-500',
  EXPIRED: 'bg-amber-100 text-amber-700',
  VOID: 'bg-red-50 text-red-600',
};

export default function MemberProfile() {
  const { t } = useT();
  const { toast } = useToast();
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, formatDate, formatDateTime } = useAppSettings();

  const [data, setData] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjBusy, setAdjBusy] = useState(false);
  const [adj, setAdj] = useState({ points: '', reason: '' });

  const load = useCallback(() => {
    loyaltyService.memberDetail(accountId)
      .then(setData)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }));
  }, [accountId, toast]);

  useEffect(() => { load(); }, [load]);

  const openCatalogue = async () => {
    setShowCatalogue(true);
    try {
      setCatalogue(await loyaltyService.catalogueForMember(accountId));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const claim = async (reward) => {
    try {
      const v = await loyaltyService.claimReward(accountId, reward.id);
      toast({ title: `${v.title} claimed`, description: `Voucher ${v.code}` });
      setShowCatalogue(false);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const adjust = async (e) => {
    e?.preventDefault();
    setAdjBusy(true);
    try {
      await loyaltyService.adjustPoints(accountId, adj.points, adj.reason);
      toast({ title: t('loyalty.member.adjust'), description: `${adj.points} pts` });
      setAdjOpen(false);
      setAdj({ points: '', reason: '' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally { setAdjBusy(false); }
  };

  const setStatus = async (status) => {
    try {
      await loyaltyService.setMemberStatus(accountId, status);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  if (!data) return <div className="p-6 text-gray-500">{t('loyalty.common.loading')}</div>;
  const m = data.member;

  return (
    <div className="p-6 w-full">
      <button type="button" onClick={() => navigate('/loyalty/members')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3">
        <ChevronRight size={15} className="rotate-180" /> {t('loyalty.members')}
      </button>

      {/* Identity + balances */}
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-600" /> {m.name}
              {m.tier && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: m.tier.colour || '#64748B' }}>{m.tier.name}</span>
              )}
              {m.status !== 'ACTIVE' && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {m.status}
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-mono">{m.membership_number}</span>
              {' · '}{m.phone}{m.email ? ` · ${m.email}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={openCatalogue} className="bg-amber-600 hover:bg-amber-700 text-white">
              <Gift size={15} className="mr-1" /> {t('loyalty.member.claim')}
            </Button>
            <Button variant="outline" onClick={() => setAdjOpen(true)}>
              {t('loyalty.member.adjust')}
            </Button>
            {m.status === 'ACTIVE'
              ? <Button variant="outline" onClick={() => setStatus('SUSPENDED')}>{t('loyalty.member.suspend')}</Button>
              : <Button variant="outline" onClick={() => setStatus('ACTIVE')}>{t('loyalty.member.reinstate')}</Button>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          {[
            [t('loyalty.member.balance'), fmtPts(m.points_balance)],
            [t('loyalty.member.lifetime'), fmtPts(m.lifetime_points)],
            [t('loyalty.member.visits'), m.visits],
            [t('loyalty.member.spend'), formatCurrency(Number(m.lifetime_spend) || 0)],
            [t('loyalty.member.since'), m.enrolled_at ? formatDate(m.enrolled_at) : '—'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-lg font-semibold text-gray-900">{value}</div>
            </div>
          ))}
        </div>

        {Number(m.expiring_points) > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle size={15} className="shrink-0" />
            {t('loyalty.member.expiring', {
              points: fmtPts(m.expiring_points),
              date: m.expiring_first_date ? formatDate(m.expiring_first_date) : '—',
            })}
          </div>
        )}
      </Card>

      {/* Vouchers */}
      <Card className="p-5 mt-4">
        <h3 className="font-semibold text-gray-800 mb-3">{t('loyalty.member.vouchers')}</h3>
        {data.vouchers.length === 0 ? (
          <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left font-medium py-2 pr-4">Code</th>
                  <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.name')}</th>
                  <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.status')}</th>
                  <th className="text-left font-medium py-2 pr-4">Expires</th>
                  <th className="text-left font-medium py-2">Used on</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.vouchers.map((v) => (
                  <tr key={v.id}>
                    <td className="py-2 pr-4 font-mono text-xs">{v.code}</td>
                    <td className="py-2 pr-4 font-medium text-gray-900">{v.title}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${VOUCHER_CLS[v.status] || ''}`}>
                        {t(`loyalty.voucher.status.${v.status}`)}
                      </span>
                    </td>
                    <td className="py-2 pr-4">{v.expires_at ? formatDate(v.expires_at) : '—'}</td>
                    <td className="py-2">{v.redeemed_order || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Ledger */}
      <Card className="p-5 mt-4">
        <h3 className="font-semibold text-gray-800 mb-3">{t('loyalty.member.ledger')}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.when')}</th>
                <th className="text-left font-medium py-2 pr-4">Type</th>
                <th className="text-right font-medium py-2 pr-4">{t('loyalty.reward.cost')}</th>
                <th className="text-right font-medium py-2 pr-4">{t('loyalty.member.balance')}</th>
                <th className="text-right font-medium py-2 pr-4">Left in lot</th>
                <th className="text-left font-medium py-2 pr-4">Expires</th>
                <th className="text-left font-medium py-2">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.transactions.map((tx) => (
                <tr key={tx.id}>
                  <td className="py-2 pr-4 whitespace-nowrap">{formatDateTime(tx.created_at)}</td>
                  <td className={`py-2 pr-4 font-medium ${KIND_CLS[tx.kind] || ''}`}>{tx.kind_label}</td>
                  <td className={`py-2 pr-4 text-right tabular-nums font-semibold ${KIND_CLS[tx.kind] || ''}`}>
                    {Number(tx.points) > 0 ? '+' : ''}{fmtPts(tx.points)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmtPts(tx.balance_after)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-500">
                    {Number(tx.points) > 0 ? fmtPts(tx.points_remaining) : ''}
                  </td>
                  <td className="py-2 pr-4">{tx.expires_on ? formatDate(tx.expires_on) : ''}</td>
                  <td className="py-2 text-gray-600">
                    {tx.order_number ? <span className="font-mono text-xs mr-1">{tx.order_number}</span> : null}
                    {tx.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Adjust dialog */}
      {adjOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !adjBusy && setAdjOpen(false)}>
          <Card className="w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('loyalty.member.adjust')}</h2>
            <form onSubmit={adjust} className="space-y-3">
              <Input type="number" placeholder="+500 / -500" required value={adj.points}
                onChange={(e) => setAdj({ ...adj, points: e.target.value })} />
              <Input placeholder={t('loyalty.member.adjustReason')} required value={adj.reason}
                onChange={(e) => setAdj({ ...adj, reason: e.target.value })} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" disabled={adjBusy}
                  onClick={() => setAdjOpen(false)}>{t('loyalty.common.cancel')}</Button>
                <Button type="submit" disabled={adjBusy}
                  className="bg-amber-600 hover:bg-amber-700 text-white">{t('loyalty.common.save')}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Catalogue dialog — the catalogue as THIS member sees it. */}
      {showCatalogue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowCatalogue(false)}>
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('loyalty.member.claim')}</h2>
            <p className="text-sm text-gray-500 mb-4">
              {t('loyalty.member.balance')}: <strong>{fmtPts(m.points_balance)}</strong>
            </p>
            {catalogue === null ? (
              <p className="text-sm text-gray-500">{t('loyalty.common.loading')}</p>
            ) : catalogue.length === 0 ? (
              <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
            ) : (
              <div className="space-y-2">
                {catalogue.map((r) => (
                  <div key={r.id}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                      r.claimable ? '' : 'opacity-60'}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {r.name}
                        <span className="ml-2 text-xs text-gray-400">
                          {t(`loyalty.reward.kind.${r.kind}`)}{r.dish_name ? ` · ${r.dish_name}` : ''}
                        </span>
                      </div>
                      {r.description && <div className="text-xs text-gray-500 truncate">{r.description}</div>}
                      {!r.claimable && (
                        <div className="text-xs text-amber-700 mt-0.5">{r.unavailable_reason}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold tabular-nums">
                        {fmtPts(r.points_cost)} {t('loyalty.reward.cost').toLowerCase()}
                      </span>
                      <Button size="sm" disabled={!r.claimable} onClick={() => claim(r)}
                        className="bg-amber-600 hover:bg-amber-700 text-white">
                        {t('loyalty.member.claim')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
